import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import telecommunications from "../../../assets/telecommunications.png";
import logonew2 from "../../../assets/logonew2.webp";
import emtvIcono from "../../../assets/EmtvPlus/EmtvIcono.png";
import logoGuentic from "../../../assets/logoGuentic.png";
import { Loader } from "../../../Loader/Loader";
import { domain } from "../../../Urls/Services/Api_urls";
import { EyeAlt, EyeClose, Copy, Check, UserCircleAlt } from "iconoir-react";
import Navbar from "../../../Gerneralcomponents/Navbar/Navbar";
import Footer from "../../../Gerneralcomponents/Footer/footer";
import "./ConfirmEMTVPlusActivation.css";

const ACTION_ACTIVATION = "activation";
const ACTION_SET_CREDENTIALS = "set_credentials";

function normalizeAction(rawAction) {
	const value = String(rawAction || "")
		.trim()
		.toLowerCase();

	if (value === ACTION_ACTIVATION) return ACTION_ACTIVATION;
	if (value === ACTION_SET_CREDENTIALS || value === "set_credential") return ACTION_SET_CREDENTIALS;
	return ACTION_SET_CREDENTIALS;
}

function getQueryValue(params, keys, fallback = "") {
	for (const key of keys) {
		const value = params.get(key);
		if (value && String(value).trim()) {
			return String(value).trim();
		}
	}
	return fallback;
}



export default function ConfirmActivateTvService() {
	const location = useLocation();
	const navigate = useNavigate();
	const inputRef = useRef(null);
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [copiedField, setCopiedField] = useState(null);

	const [form, setForm] = useState({
		firstName: "Usuario",
		lastName: "",
		tokenemtv: "",
		action: ACTION_SET_CREDENTIALS,
		username: "",
		password: "",
		confirmPassword: "",
	});

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const firstName = getQueryValue(params, ["name", "nombre", "firstName", "firstname"], "Usuario");
		const lastName = getQueryValue(params, ["lastName", "lastname", "apellido", "surname"]);
		const tokenemtv = getQueryValue(params, ["tokenemtv"]);
		const action = normalizeAction(getQueryValue(params, ["action"], ACTION_SET_CREDENTIALS));
		const username = getQueryValue(params, ["username", "userName", "usuario", "user"]);
		const password = getQueryValue(params, ["password", "pass", "contraseña"]);
		const initialUsername = action === ACTION_ACTIVATION ? username : "";
		const initialPassword = action === ACTION_ACTIVATION ? password : "";

		setForm({
			firstName,
			lastName,
			tokenemtv,
			action,
			username: initialUsername,
			password: initialPassword,
			confirmPassword: initialPassword,
		});
	}, [location.search]);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const fullName = useMemo(() => {
		return [form.firstName, form.lastName].filter(Boolean).join(" ").trim() || "Usuario";
	}, [form.firstName, form.lastName]);

	const isActivationFlow = form.action === ACTION_ACTIVATION;

	const hasMailCredentials = Boolean(form.username || form.password);

	const handleChange = (field) => (event) => {
		const value = event.target.value;
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleCopy = async (text, fieldKey) => {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(text);
			setCopiedField(fieldKey);
			setTimeout(() => setCopiedField(null), 2000);
			await Swal.fire({
				toast: true,
				position: "top-end",
				icon: "success",
				title: "Copiado con exito",
				showConfirmButton: false,
				timer: 1800,
				timerProgressBar: true,
			});
		} catch {
			setCopiedField(null);
			await Swal.fire({
				toast: true,
				position: "top-end",
				icon: "error",
				title: "No se pudo copiar",
				showConfirmButton: false,
				timer: 1800,
				timerProgressBar: true,
			});
		}
	};

	const validateForm = () => {
		const username = form.username.trim();
		const validations = [
			{ invalid: !form.tokenemtv, message: "La dirección URL no es correcta." },
			{ invalid: !username, message: "El nombre de usuario es obligatorio." },
			{ invalid: username.length < 4, message: "El nombre de usuario debe tener al menos 4 caracteres." },
			{ invalid: !form.password, message: "La contraseña es obligatoria." },
			{ invalid: form.password.length < 8, message: "La contraseña debe tener minimo 8 caracteres." },
			{
				invalid: !isActivationFlow && form.password !== form.confirmPassword,
				message: "La confirmacion de contraseña no coincide.",
			},
		];
		return validations.find((rule) => rule.invalid)?.message || "";
	};

	const getApiErrorMessage = (payload) => {
		if (!payload || typeof payload !== "object") return "";

		const nestedMessages = Array.isArray(payload?.messages)
			? payload.messages.map((item) => item?.message).filter(Boolean)
			: [];

		const candidates = [payload?.msg, payload?.message, payload?.detail, payload?.error, payload?.errors, ...nestedMessages]
			.flat()
			.filter(Boolean)
			.map((value) => String(value).trim())
			.filter(Boolean);

		return candidates[0] || "";
	};
	const sendActivationData = async ({ tokenemtv, action, username, password }) => {
		const response = await fetch(`${domain}/post2venta/emtvplus/activatepublic/`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ tokenemtv, action, username: username.trim(), password }),
		});

		const payload = await response.json().catch(() => ({}));

		if (!response.ok) {
			const apiErrorMessage = getApiErrorMessage(payload);
			const normalizedApiError = apiErrorMessage.toLowerCase();
			const tokenMessage =
				normalizedApiError.includes("token") &&
				(normalizedApiError.includes("expir") || normalizedApiError.includes("invalid") || normalizedApiError.includes("valido"));

			const statusMessage = response.status === 401
				? tokenMessage
					? "El enlace de activacion no es valido o ya expiro. Solicita uno nuevo."
					: "No autorizado para realizar esta accion."
				: response.status === 403
					? "No tienes permisos para realizar esta accion."
					: response.status >= 500
						? "Ocurrio un error en el servidor. Intenta nuevamente en unos minutos."
						: "No fue posible completar la activacion del servicio.";

			throw new Error(apiErrorMessage || statusMessage);
		}

		const payloadType = String(payload?.type || "").toLowerCase();
		const payloadStatus = Number(payload?.status);
		if (payloadType === "error" || (Number.isFinite(payloadStatus) && payloadStatus >= 400)) {
			throw new Error(getApiErrorMessage(payload) || "No fue posible completar la activacion del servicio.");
		}

		return payload;
	};

	const handleConfirm = async () => {
		const errorMessage = validateForm();
		if (errorMessage) {
			await Swal.fire({ title: "Revisa tus datos", text: errorMessage, icon: "warning" });
			return;
		}

		setLoading(true);
		try {
			const activationResult = await sendActivationData({
				tokenemtv: form.tokenemtv,
				action: form.action,
				username: form.username,
				password: form.password,
			});

			const successHtml = activationResult?.mocked
				? isActivationFlow : "Tu servicio EMTV+ fue activado con exito.";

			const result = await Swal.fire({
				title: `Activación completa, ${fullName}`,
				html: successHtml,
				icon: "success",
				confirmButtonText: "Ok",
				showDenyButton: true,
				denyButtonText: "Ir a EMTV+",
				colorDenyButton: "#0072CE",
				allowOutsideClick: false,
			});

			if (result.isDenied) {
				window.location.href = "https://app.emcali.net.co/sign-in/~/modules/home";
				return;
			}

			navigate("/login");
		} catch (error) {
			await Swal.fire({
				title: "No fue posible continuar",
				text: error?.message || "Intenta nuevamente en unos minutos.",
				icon: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Navbar />
			<div className="tv-wrapper">
			<main className="tv-page" role="main" aria-label="Activacion de servicio EMTV+">
				<section
					className="tv-hero"
					aria-hidden="true"
					style={{
						backgroundImage: `linear-gradient(120deg, rgba(16, 14, 32, 0.65), rgba(9, 12, 26, 0.15)), url(${telecommunications})`,
					}}
				>
				</section>

				<section className="tv-panel">
					<div className="tv-brand-row">
						<img src={logonew2} alt="Logo EMCALI" className="tv-brand" />
						<img src={emtvIcono} alt="Logo EMTV+" className="tv-brand-secondary" />
					</div>

					<h2 className="tv-title">Hola, {fullName}</h2>
					<p className="tv-lead">
						{isActivationFlow
							? "Estas son las credenciales recibidas para completar la activacion de tu cuenta EMTV+."
							: "Define tu nombre de usuario y contraseña para finalizar la activacion de tu cuenta EMTV+."}
					</p>

					<section className="tv-credentials" aria-label="Formulario de credenciales">
						<h2 className="tv-credentials-title">
							<UserCircleAlt className="tv-credentials-icon" />
							Credenciales EMTV+
						</h2>

						<div className="tv-credential-item">
							<label className="tv-credential-label" htmlFor="tv-username">
								NOMBRE DE USUARIO
							</label>
							<div className="tv-input-row">
								<input
									id="tv-username"
									ref={inputRef}
									className="tv-input"
									type="text"
									autoComplete="username"
									value={form.username}
									onChange={handleChange("username")}
									placeholder="ej: juan.perez"
									maxLength={40}
									readOnly={isActivationFlow}
									aria-readonly={isActivationFlow}
								/>
								{isActivationFlow ? null : (
									<button
										type="button"
										className="tv-copy-btn"
										aria-label="Copiar nombre de usuario"
										onClick={() => handleCopy(form.username, "username")}
									>
										{copiedField === "username" ? <Check className="tv-copy-icon tv-copy-icon--check" /> : <Copy className="tv-copy-icon" />}
									</button>
								)}
							</div>
						</div>

						<div className="tv-credential-item">
							<label className="tv-credential-label" htmlFor="tv-password">
								CONTRASEÑA
							</label>
							<div className="tv-input-row">
								<input
									id="tv-password"
									className="tv-input"
									type={showPassword ? "text" : "password"}
									autoComplete="new-password"
									value={form.password}
									onChange={handleChange("password")}
									placeholder="Minimo 8 caracteres"
									maxLength={60}
									readOnly={isActivationFlow}
									aria-readonly={isActivationFlow}
								/>
								<button
									type="button"
									className="tv-copy-btn"
									aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
									onClick={() => setShowPassword((prev) => !prev)}
								>
									{showPassword ? <EyeClose className="tv-copy-icon" /> : <EyeAlt className="tv-copy-icon" />}
								</button>
                
							</div>
						</div>

						{isActivationFlow ? null : (
							<div className="tv-credential-item">
								<label className="tv-credential-label" htmlFor="tv-confirm-password">
									CONFIRMAR CONTRASEÑA
								</label>
								<div className="tv-input-row">
									<input
										id="tv-confirm-password"
										className="tv-input"
										type={showConfirmPassword ? "text" : "password"}
										autoComplete="new-password"
										value={form.confirmPassword}
										onChange={handleChange("confirmPassword")}
										placeholder="Repite tu contraseña"
										maxLength={60}
									/>
									<button
										type="button"
										className="tv-copy-btn"
										aria-label={showConfirmPassword ? "Ocultar confirmacion de contraseña" : "Mostrar confirmacion de contraseña"}
										onClick={() => setShowConfirmPassword((prev) => !prev)}
									>
										{showConfirmPassword ? <EyeClose className="tv-copy-icon" /> : <EyeAlt className="tv-copy-icon" />}
									</button>
								</div>
							</div>
						)}
					</section>

					<button className="tv-cta" type="button" onClick={handleConfirm} disabled={loading}>
						Confirmar activacion
					</button>

					{!hasMailCredentials && (
						<p className="tv-warning">
							Escribe un usuario y contraseña. Luego confirma para activar tu cuenta.
						</p>
					)}

					<img src={logoGuentic} alt="Guentic" className="tv-corner" />
				</section>
			</main>
			<Footer />
			</div>
		</>
	);
}
